import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { adminService, DeliveryCompany } from '../../services/adminService';

interface FormValues {
  name: string;
  isActive?: boolean;
  sortOrder?: number;
}

const ShippingSettings: React.FC = () => {
  const [companies, setCompanies] = useState<DeliveryCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<DeliveryCompany | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<FormValues>();

  const load = async () => {
    try {
      setLoading(true);
      const list = await adminService.getDeliveryCompanies();
      setCompanies(list);
    } catch (error) {
      console.error('Failed to load delivery companies:', error);
      message.error('Failed to load delivery companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, sortOrder: 0 });
    setModalVisible(true);
  };

  const openEdit = (record: DeliveryCompany) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.Name,
      isActive: record.IsActive,
      sortOrder: record.SortOrder,
    });
    setModalVisible(true);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (editing) {
        await adminService.updateDeliveryCompany(editing.DeliveryCompanyID, values);
        message.success('Delivery company updated');
      } else {
        await adminService.createDeliveryCompany(values);
        message.success('Delivery company added');
      }
      setModalVisible(false);
      load();
    } catch (error: any) {
      if (!error?.errorFields) {
        const msg =
          error?.response?.data?.message || error?.message || 'Failed to save delivery company';
        message.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const deactivate = async (record: DeliveryCompany) => {
    try {
      await adminService.deleteDeliveryCompany(record.DeliveryCompanyID);
      message.success(`${record.Name} deactivated`);
      load();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message || error?.message || 'Failed to deactivate company';
      message.error(msg);
    }
  };

  const toggleActive = async (record: DeliveryCompany, isActive: boolean) => {
    try {
      await adminService.updateDeliveryCompany(record.DeliveryCompanyID, { isActive });
      load();
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to update';
      message.error(msg);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'Name',
      key: 'Name',
      render: (name: string) => <strong>{name}</strong>,
    },
    {
      title: 'Sort order',
      dataIndex: 'SortOrder',
      key: 'SortOrder',
      width: 120,
    },
    {
      title: 'Active',
      dataIndex: 'IsActive',
      key: 'IsActive',
      width: 120,
      render: (active: boolean, record: DeliveryCompany) => (
        <Space>
          <Switch
            size="small"
            checked={active}
            onChange={value => toggleActive(record, value)}
          />
          {active ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: unknown, record: DeliveryCompany) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
          {record.IsActive && (
            <Popconfirm
              title={`Deactivate "${record.Name}"?`}
              description="Inactive couriers won't appear in the ship picker. Past orders keep their snapshot."
              onConfirm={() => deactivate(record)}
              okText="Deactivate"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />}>
                Deactivate
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Alert
        message="Delivery companies"
        description="Manage the courier list shown when an admin marks an order as shipped. Past orders snapshot the chosen name, so deactivating a courier here does not affect history."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add delivery company
        </Button>
        <Button onClick={load}>Refresh</Button>
      </Space>

      <Table
        dataSource={companies}
        columns={columns}
        rowKey="DeliveryCompanyID"
        loading={loading}
        pagination={false}
      />

      <Modal
        title={editing ? `Edit "${editing.Name}"` : 'Add delivery company'}
        open={modalVisible}
        onOk={submit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        okText={editing ? 'Save changes' : 'Add'}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Name is required' },
              { max: 100, message: 'Name must be 100 characters or less' },
            ]}
          >
            <Input placeholder="e.g. DHL" />
          </Form.Item>
          <Form.Item
            name="sortOrder"
            label="Sort order"
            extra="Lower values appear first in the ship picker."
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ShippingSettings;
